import styles from './Rules.module.scss';
import {
	ContentExpander,
	Grid,
	NumberField,
	Timepicker
} from '@components';
import { RateCardFormErrors, RateCardSettingsTypes, RateCardTypes } from '@state';
import { ChangeEvent } from 'react';
import { useRecoilValue } from 'recoil';
import { RateCardType, PartialInputEvent, TRateCardRules } from '@types';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import { useClearRCSettingsError, useRateCardRulesState } from '@hooks';
import { getRateCardIndex } from '@utils';

type TProps = {
	isSettings?: boolean;
	isDisabled?: boolean;
	type: RateCardType;
}

export const LeadTimesSettings = (props: TProps) => {
	const {
		isSettings = false,
		isDisabled = false,
		type,
	} = props;
	const [rateCardRules, setRateCardRules] = useRateCardRulesState();
	const rules = isSettings ? rateCardRules.settings : rateCardRules.rateCard;
	const errorString = isSettings ? 'rate_card_types' : 'rate_card_prices';
	const rateCardSettingsTypes = useRecoilValue(RateCardSettingsTypes);
	const rateCardTypes = useRecoilValue(RateCardTypes);
	const rateCardTypeIndex = getRateCardIndex({
		rateCardSettingsTypes,
		rateCardTypes,
		isSettings,
		type
	});
	const rcFormErrors = useRecoilValue(RateCardFormErrors);
	const clearRCSettingsError = useClearRCSettingsError();

	const updateRules = (e: ChangeEvent<HTMLInputElement> | PartialInputEvent) => {
		const { name, value } = e.target;
		const newValue = typeof value === 'object' ?
			value.toLocaleTimeString('en-AU', {
				hour: '2-digit', minute: '2-digit', hour12: false
			}) :
			value
		const update = {
			...rules,
			[name]: name !== 'booking_cutoff' ? parseFloat(newValue) : newValue
		} as TRateCardRules
		const newRules = {
			settings: isSettings ? update : undefined,
			rateCard: !isSettings ? update : undefined
		}
		setRateCardRules(newRules);
		// @ts-ignore
		if (rcFormErrors?.[errorString]?.[rateCardTypeIndex].rules[modifierKey]) {
			clearRCSettingsError(`${errorString}.${rateCardTypeIndex}.rules.${name}.value`)
		}
	}

	const hasError = (key: string) => {
		// @ts-ignore
		return rcFormErrors?.[errorString]?.[rateCardTypeIndex]?.rules?.[key]?._errors?.[0] || '';
	}

	const getSectionErrors = () => {
		const fieldsWithPossibleError = ['booking_cutoff', 'book_start_at_days', 'max_future_book_at_days', 'min_drop_at_days']
		return fieldsWithPossibleError.map((field) =>
			hasError(field)
		).some((field) => field.length > 0);
	}

	return (
		<ContentExpander
			title={<div className={styles.expanderTitle}>Lead Times
				{
					getSectionErrors() ?
						<span className={styles.expanderErrorContainer}>
							<ErrorOutlineOutlinedIcon />
						</span>	: null
				}
			</div>}
			showSpacer
			expanded={false}
			arrowsAsExpanderIcon
			iconColor='var(--neutral-06)'
		>
			<div className={styles.expanderContent}>
				<Grid columns={2} gap={'16px'}>
					<NumberField
						label={'Pick-up date must exceed:'}
						name={'book_start_at_days'}
						value={`${rules?.book_start_at_days ?? ''}`}
						onChange={updateRules}
						keepErrorSpace={false}
						rightModule={<span className={`${styles.suffix} ${!rules?.book_start_at_days && styles.suffixPlaceholder}`}>Days</span>}
						disabled={isDisabled}
						errorMessage={hasError('book_start_at_days')}
						hasError={!!hasError('book_start_at_days')}
					/>
					<NumberField
						label={'Limit bookings to within:'}
						name={'max_future_book_at_days'}
						value={`${rules?.max_future_book_at_days ?? ''}`}
						onChange={updateRules}
						keepErrorSpace={false}
						rightModule={<span className={`${styles.suffix} ${!rules?.max_future_book_at_days && styles.suffixPlaceholder}`}>Days</span>}
						disabled={isDisabled}
						max={60}
						errorMessage={hasError('max_future_book_at_days')}
						hasError={!!hasError('max_future_book_at_days')}
					/>
					<NumberField
						label={'Drop-off date must exceed:'}
						name={'min_drop_at_days'}
						value={`${rules?.min_drop_at_days ?? 'test'}`}
						onChange={updateRules}
						keepErrorSpace={false}
						rightModule={<span className={`${styles.suffix} ${!rules?.min_drop_at_days && styles.suffixPlaceholder}`}>Days</span>}
						disabled={isDisabled}
						errorMessage={hasError('min_drop_at_days')}
						hasError={!!hasError('min_drop_at_days')}
					/>
					<Timepicker
						onChange={(e) => updateRules(e as PartialInputEvent)}
						value={`${rules?.booking_cutoff ?? ''}`}
						name={'booking_cutoff'}
						label='Booking cut-off time:'
						keepErrorSpace={false}
						disabled={isDisabled}
						errorMessage={hasError('booking_cutoff')}
						hasError={!!hasError('booking_cutoff')}
					/>
				</Grid>
			</div>
		</ContentExpander>
	);
}
